import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Schedule as ScheduledIcon,
  BarChart as StatsIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Smartphone as MobileIcon,
  Computer as WebIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { advertisingService } from '../../../services';
import type {
  Advertisement,
  AdvertisementInput,
  AdSpot,
  TipoAnunciante,
  AdvertisingStats,
  AppSpot,
} from '../../../services';
import { dayCv, todayCv } from '../../../utils/date';

const Advertising: React.FC = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  // Separador App (2): áreas das páginas de categoria da app e do site — os anúncios
  // podem ser da Pagali ou de terceiros, por isso não se filtra por anunciante.
  const isApp = tabValue === 2;
  const tipoAnunciante: TipoAnunciante = tabValue === 1 ? 'TERCEIROS' : 'PAGALI';

  // Data state
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [spots, setSpots] = useState<AdSpot[]>([]);
  const [appSpots, setAppSpots] = useState<AppSpot[]>([]);
  const [stats, setStats] = useState<AdvertisingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tablesExist, setTablesExist] = useState<boolean | null>(null); // null = not checked yet
  const [connectionError, setConnectionError] = useState(false);

  // Pagination & filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [spotFilter, setSpotFilter] = useState<string>('all');

  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [formData, setFormData] = useState<AdvertisementInput>({
    codigo: '',
    titulo: '',
    subtitulo: '',
    descricao: '',
    tipoAnunciante: 'PAGALI',
    spotId: '',
    linkUrl: '',
    dataInicio: todayCv(),
    dataFim: dayCv(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
    prioridade: 0,
    orcamento: 0,
    estado: 'A',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================
  // DATA FETCHING
  // ============================================

  const checkTables = useCallback(async () => {
    try {
      const status = await advertisingService.checkStatus();
      setTablesExist(status.allTablesExist);
      setConnectionError(!!(status as any).connectionError);
      return status.allTablesExist;
    } catch {
      setConnectionError(true);
      return false;
    }
  }, []);

  const loadAdvertisements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await advertisingService.getAdvertisements({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        spotId: spotFilter !== 'all' ? spotFilter : undefined,
        tipoAnunciante: isApp ? undefined : tipoAnunciante,
        plataforma: isApp ? 'APP' : 'OUTRAS',
      });

      if (response.success) {
        setAdvertisements(response.data);
        setTotal(response.pagination.total);
        setStats(response.stats);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar publicidades');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, spotFilter, tipoAnunciante, isApp]);

  const loadAppSpots = useCallback(async () => {
    try {
      const response = await advertisingService.getAppSpots();
      if (response.success) setAppSpots(response.data);
    } catch {
      console.error('Error loading app spots');
    }
  }, []);

  const loadSpots = useCallback(async () => {
    try {
      const response = await advertisingService.getSpots({ status: 'A' });
      if (response.success) {
        setSpots(response.data);
      }
    } catch {
      console.error('Error loading spots');
    }
  }, []);

  const setupTables = async () => {
    setLoading(true);
    try {
      const result = await advertisingService.setupTables();
      if (result.success) {
        setTablesExist(true);
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        loadAdvertisements();
        loadSpots();
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao criar tabelas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const exists = await checkTables();
      if (exists) {
        loadSpots();
        loadAppSpots();
      }
    };
    init();
  }, [checkTables, loadSpots, loadAppSpots]);

  useEffect(() => {
    // Only load if tables check completed and tables exist
    if (tablesExist === true) {
      loadAdvertisements();
    }
  }, [tablesExist, loadAdvertisements]);

  // Reset page when changing filters or tab
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, spotFilter, tabValue]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSpotFilter('all'); // os spots de um separador não existem no outro
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = async (ad?: Advertisement, spotIdInicial?: string) => {
    if (ad) {
      setSelectedAd(ad);
      // Fetch full details including image
      try {
        const response = await advertisingService.getAdvertisementById(ad.id);
        if (response.success) {
          const fullAd = response.data;
          setFormData({
            codigo: fullAd.codigo,
            titulo: fullAd.titulo,
            subtitulo: fullAd.subtitulo || '',
            descricao: fullAd.descricao || '',
            tipoAnunciante: fullAd.tipoAnunciante,
            spotId: fullAd.spotId || '',
            linkUrl: fullAd.linkUrl || '',
            entidadeId: fullAd.entidadeId || '',
            dataInicio: fullAd.dataInicio?.split('T')[0] || '',
            dataFim: fullAd.dataFim?.split('T')[0] || '',
            prioridade: fullAd.prioridade || 0,
            orcamento: fullAd.orcamento || 0,
            estado: fullAd.estado,
          });
          if (fullAd.imagem && fullAd.mimeType) {
            setImagePreview(`data:${fullAd.mimeType};base64,${fullAd.imagem}`);
          } else {
            setImagePreview(null);
          }
        }
      } catch {
        // Use basic data if fetch fails
        setFormData({
          codigo: ad.codigo,
          titulo: ad.titulo,
          subtitulo: ad.subtitulo || '',
          descricao: ad.descricao || '',
          tipoAnunciante: ad.tipoAnunciante,
          spotId: ad.spotId || '',
          linkUrl: ad.linkUrl || '',
          dataInicio: ad.dataInicio?.split('T')[0] || '',
          dataFim: ad.dataFim?.split('T')[0] || '',
          prioridade: ad.prioridade || 0,
          orcamento: ad.orcamento || 0,
          estado: ad.estado,
        });
        setImagePreview(null);
      }
    } else {
      setSelectedAd(null);
      setFormData({
        codigo: '',
        titulo: '',
        subtitulo: '',
        descricao: '',
        tipoAnunciante,
        spotId: spotIdInicial || '',
        linkUrl: '',
        dataInicio: todayCv(),
        dataFim: dayCv(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
        prioridade: 0,
        orcamento: 0,
        estado: 'A',
      });
      setImagePreview(null);
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedAd(null);
    setImagePreview(null);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        setFormData({ ...formData, imagem: base64, mimeType: file.type });
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedAd) {
        // Update
        const updateData: Partial<AdvertisementInput> = { ...formData };
        delete (updateData as any).codigo; // Don't update codigo
        await advertisingService.updateAdvertisement(selectedAd.id, updateData);
        setSnackbar({ open: true, message: 'Publicidade actualizada com sucesso', severity: 'success' });
      } else {
        // Create
        await advertisingService.createAdvertisement(formData);
        setSnackbar({ open: true, message: 'Publicidade criada com sucesso', severity: 'success' });
      }
      handleCloseEdit();
      loadAdvertisements();
      if (isApp) loadAppSpots();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ad: Advertisement) => {
    if (window.confirm(`Tem certeza que deseja eliminar "${ad.titulo}"?`)) {
      try {
        await advertisingService.deleteAdvertisement(ad.id);
        setSnackbar({ open: true, message: 'Publicidade eliminada com sucesso', severity: 'success' });
        loadAdvertisements();
        if (isApp) loadAppSpots();
      } catch (err: any) {
        setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao eliminar', severity: 'error' });
      }
    }
  };

  // ============================================
  // HELPERS
  // ============================================

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'ativo':
        return <Chip size="small" color="success" label="Activa" icon={<ActiveIcon />} />;
      case 'agendado':
        return <Chip size="small" color="info" label="Agendada" icon={<ScheduledIcon />} />;
      case 'expirado':
        return <Chip size="small" color="warning" label="Expirada" icon={<ScheduledIcon />} />;
      case 'inativo':
      default:
        return <Chip size="small" color="default" label="Inactiva" icon={<InactiveIcon />} />;
    }
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const getCTR = (cliques: number, visualizacoes: number) => {
    if (visualizacoes === 0) return '0.00';
    return ((cliques / visualizacoes) * 100).toFixed(2);
  };

  const currentStats = isApp ? stats?.app : tabValue === 0 ? stats?.pagali : stats?.terceiros;
  // Com orçamento: separador Terceiros, ou um anúncio de terceiros no separador App
  const mostraOrcamento = tabValue === 1 || (isApp && formData.tipoAnunciante === 'TERCEIROS');
  // Spots de cada separador: App = áreas das páginas; os outros = o resto
  const spotsDoSeparador: Array<{ id: string; nome: string; plataforma?: string; area?: string; largura?: number; altura?: number }> =
    isApp ? appSpots.filter((s) => s.estado === 'A') : spots.filter((s) => s.plataforma !== 'APP');
  const nomePagina = (nome: string) => nome.replace(/^App · /, '');

  // ============================================
  // RENDER - Loading initial check
  // ============================================

  if (tablesExist === null) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Gestão de Publicidade
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // ============================================
  // RENDER - Tables not setup or connection error
  // ============================================

  if (!tablesExist || connectionError) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Gestão de Publicidade
        </Typography>
        {connectionError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => checkTables()} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Tentar novamente'}
              </Button>
            }
          >
            Não foi possível conectar à base de dados. Verifique se a configuração do MySQL está correcta.
          </Alert>
        ) : (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={setupTables} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Configurar'}
              </Button>
            }
          >
            As tabelas de publicidade não estão configuradas. Clique em "Configurar" para criar as tabelas necessárias.
          </Alert>
        )}
      </Box>
    );
  }

  // ============================================
  // RENDER - Main content
  // ============================================

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Gestão de Publicidade
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAdvertisements} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            Nova Publicidade
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab
            icon={<CampaignIcon />}
            iconPosition="start"
            label="Pagali"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition="start"
            label="Terceiros"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<MobileIcon />}
            iconPosition="start"
            label="App"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>

        {/* Stats Cards */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CampaignIcon color="primary" />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{currentStats?.total || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ActiveIcon color="success" />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{currentStats?.ativas || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Activas</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatsIcon color="info" />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {(currentStats?.totalCliques || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cliques ({getCTR(currentStats?.totalCliques || 0, currentStats?.totalVisualizacoes || 0)}% CTR)
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatsIcon color="secondary" />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {(currentStats?.totalVisualizacoes || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Visualizações</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {(tabValue === 1 || isApp) && (
              <>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Orçamento Total</Typography>
                      <Typography variant="body1" fontWeight={700} color="primary">
                        {formatCurrency((currentStats as any)?.totalOrcamento || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Gasto</Typography>
                      <Typography variant="body1" fontWeight={700} color="success.main">
                        {formatCurrency((currentStats as any)?.totalGasto || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Separador App: uma área por página de categoria */}
        {isApp && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Páginas</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Cada página de categoria tem uma área de publicidade no fundo, na app e no site
              (pagali.cv › Pagamentos). Só aparece quando tem anúncios activos. Imagem recomendada:
              1200 × 400 px. Com mais do que um anúncio, rodam em carrossel.
            </Typography>
            <Grid container spacing={1.5}>
              {appSpots.filter((sp) => sp.estado === 'A').map((sp) => (
                <Grid key={sp.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      borderColor: spotFilter === sp.id ? 'primary.main' : 'divider',
                      bgcolor: spotFilter === sp.id ? 'action.selected' : undefined,
                    }}
                    onClick={() => setSpotFilter(spotFilter === sp.id ? 'all' : sp.id)}
                  >
                    <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{nomePagina(sp.nome)}</Typography>
                          <Typography variant="caption" color={sp.anunciosNoAr > 0 ? 'success.main' : 'text.secondary'}>
                            {sp.anunciosNoAr > 0
                              ? `${sp.anunciosNoAr} no ar${sp.totalAnuncios > sp.anunciosNoAr ? ` · ${sp.totalAnuncios} no total` : ''}`
                              : sp.totalAnuncios > 0 ? `${sp.totalAnuncios} sem nenhum no ar` : 'Vazia'}
                          </Typography>
                        </Box>
                        <Tooltip title={`Novo anúncio em ${nomePagina(sp.nome)}`}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(undefined, sp.id); }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar publicidades..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={isApp ? 'Página' : 'Área/Spot'}
                value={spotFilter}
                onChange={(e) => setSpotFilter(e.target.value)}
              >
                <MenuItem value="all">{isApp ? 'Todas as páginas' : 'Todas as áreas'}</MenuItem>
                {spotsDoSeparador.map((spot) => (
                  <MenuItem key={spot.id} value={spot.id}>
                    {isApp || spot.plataforma === 'MOBILE' ? <MobileIcon sx={{ mr: 1, fontSize: 16 }} /> : <WebIcon sx={{ mr: 1, fontSize: 16 }} />}
                    {isApp ? nomePagina(spot.nome) : `${spot.nome} (${advertisingService.getAreaLabel(spot.area || '')})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="A">Activas</MenuItem>
                <MenuItem value="I">Inactivas</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Publicidade</TableCell>
                <TableCell>Área</TableCell>
                <TableCell>Período</TableCell>
                <TableCell align="center">Cliques</TableCell>
                <TableCell align="center">Views</TableCell>
                <TableCell align="center">CTR</TableCell>
                {(tabValue === 1 || isApp) && <TableCell align="right">Orçamento</TableCell>}
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={tabValue === 1 || isApp ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : advertisements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tabValue === 1 || isApp ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {isApp ? 'Nenhum anúncio nesta página — use o + numa página acima' : 'Nenhuma publicidade encontrada'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                advertisements.map((ad) => (
                  <TableRow key={ad.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ad.temImagem ? (
                          <ImageIcon color="primary" fontSize="small" />
                        ) : (
                          <CampaignIcon color="disabled" fontSize="small" />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {ad.titulo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ad.codigo}{isApp ? ` · ${ad.tipoAnunciante === 'TERCEIROS' ? 'Terceiros' : 'Pagali'}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {ad.spotNome ? (
                        <Tooltip title={`${ad.spotPlataforma} - ${advertisingService.getAreaLabel(ad.spotArea || '')}`}>
                          <Chip
                            size="small"
                            icon={ad.spotPlataforma === 'MOBILE' || ad.spotPlataforma === 'APP' ? <MobileIcon /> : <WebIcon />}
                            label={isApp ? nomePagina(ad.spotNome) : ad.spotNome}
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {ad.dataInicio ? new Date(ad.dataInicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '-'}
                        <br />
                        {ad.dataFim ? new Date(ad.dataFim).toLocaleDateString('pt-CV', { timeZone: 'UTC' }) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {ad.cliques.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {ad.visualizacoes.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${getCTR(ad.cliques, ad.visualizacoes)}%`}
                        color={parseFloat(getCTR(ad.cliques, ad.visualizacoes)) > 5 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    {(tabValue === 1 || isApp) && (
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(ad.gasto || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / {formatCurrency(ad.orcamento || 0)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>{getStatusChip(ad.statusCampanha)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(ad)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(ad)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon />
          {selectedAd ? 'Editar Publicidade' : 'Nova Publicidade'}
          {isApp ? ' (App)' : tabValue === 0 ? ' (Pagali)' : ' (Terceiros)'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Código"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                disabled={!!selectedAd}
                helperText={selectedAd ? 'Código único' : 'Opcional — gerado se ficar vazio'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Título"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Subtítulo"
                value={formData.subtitulo}
                onChange={(e) => setFormData({ ...formData, subtitulo: e.target.value })}
                helperText="Texto secundário exibido abaixo do título"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={isApp ? 'Página' : 'Área de Exibição'}
                value={formData.spotId}
                onChange={(e) => setFormData({ ...formData, spotId: e.target.value })}
                required={isApp}
                helperText={isApp ? 'Aparece no fundo desta página, na app e no site' : undefined}
              >
                {!isApp && <MenuItem value="">Nenhuma área específica</MenuItem>}
                {spotsDoSeparador.map((spot) => (
                  <MenuItem key={spot.id} value={spot.id}>
                    {isApp ? `📱 ${nomePagina(spot.nome)}` : (
                      <>
                        {spot.plataforma === 'MOBILE' ? '📱 ' : '💻 '}
                        {spot.nome}{spot.largura && spot.altura ? ` (${spot.largura}x${spot.altura})` : ''}
                      </>
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {isApp && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Anunciante"
                  value={formData.tipoAnunciante}
                  onChange={(e) => setFormData({ ...formData, tipoAnunciante: e.target.value as TipoAnunciante })}
                >
                  <MenuItem value="PAGALI">Pagali</MenuItem>
                  <MenuItem value="TERCEIROS">Terceiros</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="URL de Destino"
                value={formData.linkUrl}
                onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                placeholder="https://… (abre ao tocar no anúncio)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Data Início"
                value={formData.dataInicio}
                onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Data Fim"
                value={formData.dataFim}
                onChange={(e) => setFormData({ ...formData, dataFim: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Prioridade"
                value={formData.prioridade}
                onChange={(e) => setFormData({ ...formData, prioridade: parseInt(e.target.value) || 0 })}
                helperText="Maior = mais prioridade"
              />
            </Grid>
            {mostraOrcamento && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Orçamento (CVE)"
                  value={formData.orcamento}
                  onChange={(e) => setFormData({ ...formData, orcamento: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: mostraOrcamento ? 6 : 12 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">Activa</MenuItem>
                <MenuItem value="I">Inactiva</MenuItem>
              </TextField>
            </Grid>

            {/* Image Upload */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Imagem da Publicidade
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
                component="label"
              >
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
                {imagePreview ? (
                  <Box>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Clique para substituir
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Clique para carregar uma imagem
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isApp ? 'PNG ou JPG, 1200 × 400 px (3:1), até 2MB' : 'PNG, JPG até 2MB'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.titulo || (isApp && !formData.spotId) || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {selectedAd ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Advertising;
